import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { useDetailNavigation } from "../../hooks/useDetailNavigation";
import EmptySectionState from "../../components/EmptySectionState";
import { getContentLabel } from "../../utils/utils";
import Carousel from "../../components/Carousel";
import CollectionCarouselCard from "../explore/CoverCollection";
import PlaceholderFade from "../../components/PlaceholderFade";
import { CurateIcon } from "../../components/icons";

const MAX_COLLECTIONS = 10;
const POSTERS_PER_CARD = 5; // CoverCollection's poster stack

/**
 * The profile's collections row: the user's public collections for this content
 * type (or every type, for "all"), most recently updated first, linking through to their Curate page.
 * Owners see exactly what visitors see, plus a muted empty state pointing to
 * Curate when there are none; visitors don't get an empty one (routes/Profile.js
 * hides it).
 */
export default function ProfileCollections({
  username,
  contentType,
  count,
  isOwnProfile,
}) {
  const { fetchUserLists } = useApi();
  const { openList } = useDetailNavigation();
  const navigate = useNavigate();

  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ["profileCollections", username, contentType],
    queryFn: () =>
      fetchUserLists({
        username,
        contentType,
        sortBy: "updated",
        perPage: MAX_COLLECTIONS,
        maxItems: POSTERS_PER_CARD,
      }),
    enabled: !!username,
    // Collection edits invalidate the Curate page's queries, not this one —
    // refetch on each profile visit instead
    staleTime: 0,
    // Keep the previous content type's row up while switching, so the page
    // doesn't collapse and lose the scroll position — same user only
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === username ? previousData : undefined,
  });

  // Don't flash the empty state before the first response
  if (isLoading) return null;

  const lists = data?.items ?? [];
  const curatePath = `/curate/u/${username}`;
  // Blank for "all", leaving e.g. "Your public collections"
  const typeLabel =
    contentType === "all"
      ? ""
      : `${getContentLabel({ type: contentType, lowercase: true })} `;

  // API list shape → CoverCollection card props (as in Explore)
  const cardItems = lists.map((list) => ({
    list_id: list.list_id,
    title: list.title,
    contentType: list.content_type,
    itemCount: list.item_count ?? list.items.length,
    finishedCount: list.finished_count ?? null,
    posters: list.items.slice(0, POSTERS_PER_CARD).map((i) => i.image_url),
  }));

  // Says where collections are made, and that only public ones show here
  const emptyState = isOwnProfile && (
    <EmptySectionState
      message={`Create ${typeLabel}collections in Curate — public ones show up here`}
      actionLabel="Go to Curate"
      onAction={() => navigate(curatePath, { state: { contentType } })}
    />
  );

  const collectionCount = count ?? lists.length;

  return (
    <PlaceholderFade active={isPlaceholderData}>
      <Carousel
        title={`${collectionCount.toLocaleString()} ${
          collectionCount === 1 ? "collection" : "collections"
        } curated`}
        items={cardItems}
        getItemKey={(card) => card.list_id}
        renderItem={(card) => (
          <CollectionCarouselCard
            {...card}
            onClick={() =>
              openList({ list_id: card.list_id, title: card.title })
            }
          />
        )}
        seeMoreLink={lists.length > 0 ? curatePath : undefined}
        seeMoreState={{ contentType }}
        emptyState={emptyState}
        titleIcon={<CurateIcon color="primary" />}
        // The row holds MAX_COLLECTIONS; only end with a "See all" card if there are more
        showSeeMoreCard={collectionCount > MAX_COLLECTIONS}
      />
    </PlaceholderFade>
  );
}
